import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Minimum number of characters for a newly set password. Mirrors the backend
 * rule; applies wherever a password is set, not to passwords already stored.
 */
export const PASSWORD_MIN_LENGTH = 12;

export const EMAIL_REGEX = new RegExp(
  /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
);

export function isEmailValid(email: string): boolean {
  return EMAIL_REGEX.test(email);
}

export function positiveNumbers(event: KeyboardEvent): boolean {
  const charCode = event.which ? event.which : event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
}

export function clampFormControl(
  control: AbstractControl | null,
  max: number,
  min = 1,
): void {
  if (!control) return;
  const value = Number(control.value);
  if (value > max) {
    control.setValue(max);
  } else if (value < min && control.value !== null && control.value !== '') {
    control.setValue(min);
  }
}

export const httpUrlValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;
  if (value.includes('\\')) return { invalidUrl: true };
  if (!/^https?:\/\//i.test(value)) return { invalidUrl: true };
  try {
    const url = new URL(value);
    const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
    return isHttp && url.hostname ? null : { invalidUrl: true };
  } catch {
    return { invalidUrl: true };
  }
};

export const passwordStrengthValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value;
  if (!value) return null;

  const hasUpperCase = /[A-Z]/.test(value);
  const hasLowerCase = /[a-z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>/_\-=+\[\];'~]/.test(value);
  const hasMinLength = value.length >= PASSWORD_MIN_LENGTH;

  const passwordValid =
    hasUpperCase && hasLowerCase && hasNumber && hasSymbol && hasMinLength;

  return !passwordValid ? { passwordStrength: true } : null;
};

export function passwordMatchValidator(
  passwordControl: AbstractControl,
): ValidatorFn {
  return (confirm: AbstractControl): ValidationErrors | null =>
    confirm.value === passwordControl.value ? null : { passwordMismatch: true };
}

export function createPasswordControl(): FormControl<string> {
  return new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, passwordStrengthValidator],
  });
}

export function createConfirmPasswordControl(
  passwordControl: FormControl<string>,
): FormControl<string> {
  const confirm = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, passwordMatchValidator(passwordControl)],
  });
  passwordControl.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe(() => confirm.updateValueAndValidity());
  return confirm;
}
